/* eslint-disable */
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Eye, Pencil, Trash2, Filter, CheckCircle2 } from 'lucide-react';
import { Tooltip } from '@mui/material';
import { Button } from '@/Components/ui/button';
import { urls } from '@/Constants';
import { purchaseRequestServices } from '@/Services/PurchaseRequestServices/PurchaseRequestService';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import { useSelector, useDispatch } from 'react-redux';
import userRolesAndPermissions from '@/Services/userRolesAndPermissions.services';
import { useAuth } from '@/Components/Auth/Login/auth';
import { formActions, modalActions } from '@/Actions';
import PRApprovalModalContent from './PRApprovalModalContent';
import { CustomModal } from '@/Components/Modals/CustomModal';
import wastageAction from '@/Actions/wastage.action';
import PurchaseRequestCreate from './PurchaseRequestCreate';
import { AlertModal } from '@/Components/Modals';
import { getHigherUnitAndQuantity, getHigherUnitName, filtersInfoChip, useProductData } from '@/Helpers';
import moment from 'moment';
import { notificationServices } from '@/Services';
import drawerActions from '@/Actions/drawer.actions';
import ImagesFinder from '@/Components/ImagesFinder/ImagesFinder';
import GlobalSearch from '@/Components/globalSearch/search';


const columns = [
    {
        title: 'ID',
        key: 'id',
        width: 50,
    },
    {
        title: 'ClientPrId',
        key: 'clientPrId',
        width: 80,
    },
    {
        title: 'PR NAME',
        key: 'prName',
        width: 100,
    },
    {
        title: 'STORE NAME',
        key: 'storeName',
        width: 100,
    },
    {
        title: 'CREATED AT',
        key: 'createdAt',
        width: 80,
    },
    {
        title: 'REQUIRE BY',
        key: 'requireByDate',
        width: 80,
    },
    {
        title: 'REQUESTED BY',
        key: 'requestedBy',
        width: 100,
    },
    {
        title: 'Action By',
        key: 'actionBy',
        width: 100,
    },
    {
        title: 'STATUS',
        key: 'status',
        width: 100,
    },
    {
        title: 'Action',
        key: 'action',
        width: 100,
    },
];


function PurchaseRequestList() {
    const history = useHistory();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { userConfig = {} } = useAuth();
    const { level } = userConfig?.userDetails || {};
    const { userRolesDict = {} } = userConfig || {};
    const storeList = useSelector((state) => state.form?.FILTERED_STORES?.data?.LIST || []);
    const usersList = useSelector((state) => state.wastage?.usersList || []);
    const productList = useSelector((state) => state.form?.ALL_PRODUCTS?.data?.LIST || []);
    const [modalMode, setModalMode] = useState('edit');
    const [filters, setFilters] = useState({
        dateRange: {
            from: moment().toDate(),
            to: moment().toDate(),
        },
        store: { id: -999, name: 'ALL' },
    });
    const [searchValue, setSearchValue] = useState('');

    const handleFetchAuditList = (newFilters) => {
        setFilters(newFilters);
        queryClient.invalidateQueries({ queryKey: ['purchaseRequests'] });
    };

    const filtersInfo = useMemo(() => ({
        header: {
            store: false,
            date: false,
        },
        store: { text: 'Store', show: true },
        dateRangeOnly: { text: 'Date Range', show: true },
    }), []);

    const productOptions = useMemo(() => {
        const data = [];
        productList?.forEach((product) => {
            product?.variants?.forEach((variant) => {
                data.push({
                    label: `${product?.productName}(${variant?.displayName})${variant?.clientVariantId ? ` - ${variant?.clientVariantId}` : ''}`,
                    value: variant?.id,
                    variantId: variant?.id,
                    productId: product?.id,
                    productName: product?.productName,
                    displayName: variant?.displayName,
                    skuCode: variant?.clientVariantId || product?.skuCode,
                    unit: product?.unit,
                    skuType: product?.skuType,
                });
            });
        });
        return data;
    }, [productList]);

    const { data: prResponse, isLoading: isLoadingPRs, refetch } = useQuery({
        queryKey: ['purchaseRequests', filters],
        queryFn: () => purchaseRequestServices.fetchPurchaseRequestList({
            data: {
                filters: {
                    storeId: filters.store?.id !== -999 ? filters.store?.id : null,
                    dateRangeFilter: {
                        from: filters.dateRange?.from ? moment(filters.dateRange.from).format('YYYY-MM-DD') : null,
                        to: filters.dateRange?.to ? moment(filters.dateRange.to).format('YYYY-MM-DD') : null,
                    },
                }
            }
        }),
        staleTime: Infinity,
    });

    const { data: usersResponse, isLoading: isLoadingUsers } = useQuery({
        queryKey: ['usersList'],
        queryFn: () => userRolesAndPermissions.getUsersList(),
        staleTime: usersList?.length > 0 ? Infinity : 0,
    });

    const { mutate: updateStatus, isLoading: isUpdating } = useMutation({
        mutationFn: purchaseRequestServices.updatePurchaseRequestStatus,
        onSuccess: (res) => {
            if (res.statusCode === 200) {
                notificationServices.generateNotification({
                    type: 'success',
                    message: 'Purchase Request updated successfully',
                });
                queryClient.invalidateQueries({ queryKey: ['purchaseRequests'] });
                refetch();
                dispatch(modalActions.toggleCustomModal(false));
            }
        },
        onError: (err) => {
            notificationServices.generateNotification({
                type: 'error',
                message: err.message || 'Operation failed',
            });
        }
    });

    const { mutate: fetchPrDetails, isLoading: isLoadingDetail } = useMutation({
        mutationFn: purchaseRequestServices.fetchPurchaseRequestDetail,
        onSuccess: (res) => {
            if (res.statusCode === 200) {
                const data = res.data || {};
                const fields = {};
                fields.selectedStoreId = String(data?.storeId);
                fields.selectedProcessId = String(data?.processId);
                fields.prName = data?.jsonData?.description;
                fields.prDate = moment(data.createdAt).format('YYYY-MM-DD');
                fields.requireByDate = data?.jsonData?.requireByDate
                    ? moment(data.jsonData.requireByDate).toDate()
                    : null;
                fields.prId = data?.id;
                fields.requestedBy = data?.requestedBy;
                fields.uploadedDoc = data?.jsonData?.uploadedDoc || '';
                fields.uploadedDocFileName = data?.jsonData?.uploadedDocFileName || '';
                fields.selectedProducts = data?.items?.map((item) => {
                    const selected = productOptions?.find(
                        (opt) => opt.variantId === item.entityId
                    );
                    let prJsonData = item.jsonData;
                    if (typeof prJsonData === 'string') {
                        try {
                            prJsonData = JSON.parse(prJsonData);
                        } catch (e) {
                            prJsonData = {};
                        }
                    }
                    const availableQty = prJsonData?.availableQty ?? item.qty;

                    return {
                        ...selected,
                        id: selected?.variantId,
                        qty: getHigherUnitAndQuantity(selected?.unit, availableQty)?.[0],
                        unit: getHigherUnitName(selected?.unit),
                        remarks: prJsonData?.remarks ?? '',
                    };
                });
                const isViewOnly = modalMode === 'view';
                dispatch(modalActions.toggleCustomModal(true, {
                    width: '100%',
                    height: '100%',
                    bodyClass: 'p-0',
                    header: (
                        <div className='w-full flex items-center justify-center'>
                            <div className='w-6' /> {/* Left spacer to balance close button */}
                            <h3 className='text-lg font-bold text-gray-900 flex-1 text-center'>
                                {isViewOnly ? 'View Purchase Request' : 'Edit Purchase Request'}
                            </h3>
                        </div>
                    ),
                    headerClass: 'px-4 pt-2 border-none',
                    footerClass: 'border-t border-gray-100 w-full flex justify-end',
                    actionButtons: isViewOnly ? [] : [
                        {
                            label: 'Edit Purchase Request',
                            onClick: () => window.dispatchEvent(new CustomEvent('SUBMIT_PURCHASE_REQUEST')),
                            className: 'bg-yellow-500 hover:bg-yellow-600 text-gray-800 font-bold py-2.5 px-8 rounded-md text-base'
                        }
                    ],
                    bodyContent: <PurchaseRequestCreate refetch={refetch} editMode={!isViewOnly} initialData={fields} viewMode={isViewOnly} />,
                }))
            }
        }
    });

    const handleAction = (row) => {
        updateStatus({
            data: {
                action: 'CANCEL',
                entityType: 'PurchaseRequest',
                entityId: row.id,
            }
        });
    };

    useEffect(() => {
        if (storeList?.length === 0) {
            dispatch(formActions.gateway({
                entity: 'FILTERED_STORES',
                req: {
                    params: {
                        id: '',
                    },
                },
                method: 'LIST',
            }));
        }
        if (productList?.length === 0) {
            useProductData(dispatch);
        }
    }, []);

    useEffect(() => {
        if (usersResponse?.data?.length > 0) {
            dispatch(wastageAction.setUsersList(usersResponse?.data));
        }
    }, [usersResponse]);


    const prList = useMemo(() => {
        const list = prResponse?.data || [];
        if (!searchValue) return list;
        const searchLower = searchValue.toLowerCase();
        return list.filter(item =>
            String(item.id).includes(searchLower) ||
            item.clientPrId?.toLowerCase().includes(searchLower) ||
            item.jsonData?.description?.toLowerCase().includes(searchLower)
        );
    }, [prResponse?.data, searchValue]);
    const isLoading = isLoadingPRs || isLoadingUsers;


    const handleClosePr = (row) => {
        dispatch(modalActions.toggleAlert(true, {
            heading: 'Cancel Purchase Request',
            message: `Are you sure you want to Cancel PR ${row?.clientPrId ? `(${row?.clientPrId})` : ''}?`,
            confirmText: 'Yes, Cancel',
            cancelText: 'No',
            handleConfirm: () => {
                dispatch(modalActions.toggleAlert(false));
                handleAction(row);
            },
        }));
    }


    const customRowData = {
        id: (row) => <p className='flex justify-start text-gray-500 font-mono text-xs'>{row?.id}</p>,
        clientPrId: (row) => <p className='flex justify-start text-gray-500 font-mono text-xs'>{row?.clientPrId ?? '-'}</p>,
        prName: (row) => <p className='flex justify-start font-medium text-gray-700'>{row?.jsonData?.description || '-'}</p>,
        requestedBy: (row) => {
            const user = usersList.find(u => u.id === row?.requestedBy);
            return <p className='flex justify-start text-gray-600'>{user?.displayName || '-'}</p>;
        },
        storeName: (row) => {
            const store = storeList.find(s => s.id === row?.storeId);
            return <p className='flex justify-start text-gray-600'>{store?.name || '-'}</p>;
        },
        status: (row) => {
            const isClosed = row?.status === 'CLOSED';
            const isApproved = row?.approvalStatus?.isFullyApproved;
            const isRejected = row?.approvalStatus?.isPermanentlyRejected;
            const isCancelled = row?.approvalStatus?.isCancelled;
            const pendingRoles = row?.approvalStatus?.currentPendingRoles || [];

            let status = 'Pending Approval';
            let colorClass = 'bg-blue-50 text-blue-700 border-blue-200';

            if (isClosed) {
                status = 'Closed';
                colorClass = 'bg-slate-200 text-slate-700 border-slate-400';
            } else if (isCancelled) {
                status = 'Cancelled';
                colorClass = 'bg-gray-100 text-gray-600 border-gray-300';
            } else if (isApproved) {
                status = 'Approved';
                colorClass = 'bg-green-50 text-green-700 border-green-200';
            } else if (isRejected) {
                status = 'Rejected';
                colorClass = 'bg-red-50 text-red-700 border-red-200';
            } else if (pendingRoles.length > 0) {
                status = `Pending Approval — ${pendingRoles.join(', ')}`;
            }

            return (
                <p className='flex justify-start'>
                    <span className={`px-3 py-1 rounded-full border text-[11px] font-bold inline-block ${colorClass}`}>
                        {status}
                    </span>
                </p>
            );
        },
        requireByDate: (row) => {
            const requireBy = row?.jsonData?.requireByDate;
            return (
                <p className='flex justify-start font-medium text-gray-700'>
                    {requireBy ? moment(requireBy).format('DD MMM YYYY') : '-'}
                </p>
            );
        },
        createdAt: (row) => {
            if (!row.createdAt) return '-';
            const date = new Date(row.createdAt);
            return (
                <p className='flex flex-col justify-start items-start text-[11px] text-gray-500'>
                    <span className='font-bold text-gray-700'>{date.toLocaleDateString()}</span>
                    <span>{date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </p>
            );
        },
        actionBy: (row) => {
            const isApproved = row.approvalStatus?.isFullyApproved;
            const isRejected = row.approvalStatus?.isPermanentlyRejected;
            const isCancelled = row.approvalStatus?.isCancelled;

            const logs = row?.approvalStatus?.logs || [];
            const lastLog = logs.length > 0 ? logs[logs.length - 1] : null;

            const userName = lastLog
                ? usersList?.find((item) => item?.id === lastLog?.userId)?.displayName
                : null;

            if (isApproved) {
                return (
                    <p className='w-full text-left text-[10px] text-green-600 uppercase font-semibold tracking-wider'>
                        Approved By: {userName}
                    </p>
                );
            }

            if (isRejected) {
                return (
                    <p className='w-full text-left text-[10px] text-red-500 uppercase font-semibold tracking-wider'>
                        Rejected By: {userName}
                    </p>
                );
            }

            if (isCancelled) {
                return (
                    <p className='w-full text-left text-[10px] text-gray-500 uppercase font-semibold tracking-wider'>
                        Cancelled By: {userName}
                    </p>
                );
            }

            return null;
        },
        action: (row) => {
            const isClosed = row?.status === 'CLOSED';
            if (isClosed) {
                return (
                    <div className='flex flex-row gap-2 justify-start items-start w-full'>
                        <Tooltip title='View PR' arrow>
                            <Button
                                variant='ghost'
                                size='icon'
                                className='h-8 w-8 bg-blue-100 text-blue-600 hover:text-white hover:bg-blue-600'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setModalMode('view');
                                    fetchPrDetails({ data: { prId: row?.id } });
                                }}
                            >
                                <Eye size={18} />
                            </Button>
                        </Tooltip>
                    </div>
                );
            }
            const pendingLevel = row.approvalStatus?.currentPendingUserLevel;
            const pendingRoles = row.approvalStatus?.currentPendingRoles || [];
            const userRoles = Object.values(userRolesDict);
            const isLevelMatch = pendingLevel ? String(level) === String(pendingLevel) : true;
            const isRoleMatch = pendingRoles.some(role => userRoles.includes(role));
            const isFinished = row.approvalStatus?.isFullyApproved || row.approvalStatus?.isPermanentlyRejected;
            const isCancel = row?.approvalStatus?.isCancelled;
            const isEdit = row?.approvalStatus?.editAllowed;

            return (
                <div className='flex flex-col gap-1.5 justify-start items-start w-full'>
                    {!isFinished && !isCancel && !(isLevelMatch && isRoleMatch) && (
                        <p className='w-full text-left m-0'>
                            <span className='text-[10px] text-gray-600 uppercase font-semibold tracking-wider'>
                                Pending Approval
                                {pendingRoles.length > 0 && (
                                    <span className='text-gray-500 normal-case'>
                                        {' '}— {pendingRoles.join(', ')}
                                    </span>
                                )}
                            </span>
                        </p>
                    )}
                    <div className='flex flex-row gap-2 justify-start items-start w-full'>
                        {!isFinished && isLevelMatch && isRoleMatch && (
                            <Tooltip title='Approve PR' arrow>
                                <Button
                                    variant='ghost'
                                    size='icon'
                                    className='h-8 w-8 bg-emerald-100 text-emerald-600 hover:text-white hover:bg-emerald-600'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        dispatch(modalActions.toggleCustomModal(true, {
                                            width: '95vw',
                                            height: '95vh',
                                            bodyClass: 'p-0 h-full',
                                            hideFooter: true,
                                            bodyContent: <PRApprovalModalContent rowData={row} refetch={refetch} />,
                                        }));
                                    }}
                                >
                                    <CheckCircle2 size={18} />
                                </Button>
                            </Tooltip>
                        )}
                        <Tooltip title='View PR' arrow>
                            <Button
                                variant='ghost'
                                size='icon'
                                className='h-8 w-8 bg-blue-100 text-blue-600 hover:text-white hover:bg-blue-600'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setModalMode('view');
                                    fetchPrDetails({ data: { prId: row?.id } });
                                }}
                            >
                                <Eye size={18} />
                            </Button>
                        </Tooltip>
                        {isEdit && (
                            <Tooltip title='Edit PR' arrow>
                                <Button
                                    variant='ghost'
                                    size='icon'
                                    className='h-8 w-8 bg-amber-100 text-amber-500 hover:text-white hover:bg-amber-500'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setModalMode('edit');
                                        fetchPrDetails({ data: { prId: row?.id } });
                                    }}
                                >
                                    <Pencil size={18} />
                                </Button>
                            </Tooltip>
                        )}
                        {!isCancel && level !== 1 && (
                            <Tooltip title='Cancel PR' arrow>
                                <Button
                                    variant='ghost'
                                    size='icon'
                                    className='h-8 w-8 bg-red-100 text-red-500 hover:text-white hover:bg-red-500'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClosePr(row);
                                    }}
                                >
                                    <Trash2 size={18} />
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                </div>
            );
        },
    };

    const handleCustomCol = useMemo(() => (column) => {
        return (
            <p className='flex justify-center text-[14px] w-full uppercase'>
                {column.title}
            </p>
        );
    }, []);

    return (
        <div className='bg-white rounded-lg shadow-sm p-4'>
            <CustomModal />
            <AlertModal />
            <div className='flex justify-between items-center mb-6'>
                <h1 className='text-xl font-bold text-gray-800 tracking-tight'>Purchase Requests</h1>
                <div className='flex items-center gap-3'>
                    <Button
                        variant='outline'
                        className='text-white bg-teal-600 border border-gray-300 rounded-md hover:bg-teal-700 hover:text-white px-4 h-9 gap-2 flex items-center text-sm shadow-sm font-bold'
                        onClick={() => dispatch(drawerActions.toggleFiltersDrawer(true))}
                    >
                        <Filter size={16} />
                        <span>Filters</span>
                    </Button>
                    <Button
                        className='bg-yellow-400 hover:bg-yellow-500 text-black px-4 h-9 gap-2 flex items-center text-sm shadow-sm font-bold transition-colors'
                        onClick={() => dispatch(modalActions.toggleCustomModal(true, {
                            width: '100%',
                            height: '100%',
                            bodyClass: 'p-0',
                            header: (
                                <div className='w-full flex items-center justify-center'>
                                    <div className='w-6' /> {/* Left spacer to balance close button */}
                                    <h3 className='text-lg font-bold text-gray-900 flex-1 text-center'>Create Purchase Request</h3>
                                </div>
                            ),
                            headerClass: 'px-4 pt-2 border-none',
                            footerClass: 'border-t border-gray-100 w-full flex justify-end',
                            actionButtons: [
                                {
                                    label: 'Create Purchase Request',
                                    onClick: () => window.dispatchEvent(new CustomEvent('SUBMIT_PURCHASE_REQUEST')),
                                    className: 'bg-yellow-500 hover:bg-yellow-600 text-gray-800 font-bold py-2.5 px-8 rounded-md text-base'
                                }
                            ],
                            bodyContent: <PurchaseRequestCreate refetch={refetch} />,
                        }))}
                    >
                        <Plus size={16} />
                        <span>Raise PR</span>
                    </Button>
                </div>
            </div>
            <div className='flex items-baseline justify-between gap-4 mb-2'>
                <div className='flex-1 min-w-0'>
                    <GlobalSearch
                        searchDropDown={false}
                        setSearchValue={setSearchValue}
                        placeholderText='Search PR...'
                    />
                </div>
                <div className='flex-shrink-0'>
                    {filtersInfoChip(filtersInfo, filters)}
                </div>
            </div>
            <div className='border border-gray-200 rounded-md overflow-auto h-[calc(100vh-250px)]'>
                <VariantTable
                    data={prList}
                    columns={columns}
                    customRowData={customRowData}
                    isLoading={isLoading}
                    customInfoLine='No purchase requests found.'
                />
            </div>
            <ImagesFinder
                onSubmit={handleFetchAuditList}
                filtersInfo={filtersInfo}
                exportInfo={{ hideExportAccordian: true }}
                avoidInitialCall={true}
            />
        </div>
    );
}

export default PurchaseRequestList;